import React, { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import exerciseRowStyles from './ExerciseRow.module.scss';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import EmptyState from '../composites/EmptyState';
import ContentDrill from '../composites/ContentDrill';
import ExerciseRow from './ExerciseRow';
import ListContent from '../composites/ListContent';
import BaseExerciseDialog from './BaseExerciseDialog';
import { isMotivisionTrainer } from '../../utils/dashboardUtils';
import {
  useGlobalData,
  useSessionAction,
  useSessionData,
  useUserAction,
} from '../../providers';
import { fetchMergedExercisesPageByTrainerId, fetchExerciseById } from '../../api';
import { queryKeys } from '../../api/queryKeys';
import { usePaginatedListQuery } from '../../hooks/usePaginatedListQuery';
import { removeItemFromPaginatedQueries } from '../../api/pagination';

import type { Exercise, WithOptionalId } from '../../types';

const exerciseListTitles = [
  'Exercise Name',
  'Equipment Type',
  'Muscle Group',
  'Movement Pattern',
  'Created By',
  'Options',
];

export interface AddExerciseModalData {
  baseExercise: Exercise | null;
  isCreatingPersonalizedExercise?: boolean;
}

const TrainerExercisesContent: React.FC = () => {
  const { updateExercise, createExercise, deleteUserExercise } =
    useUserAction();
  const { ensureExerciseLibraryLoaded } = useSessionAction();
  const queryClient = useQueryClient();
  const { loginTrainer, trainers } = useSessionData();
  const { muscles } = useGlobalData();
  const [selectedExercise, setSelectedExercise] = useState<string>('');
  const [addExerciseModalData, setAddExerciseModalData] =
    useState<AddExerciseModalData | null>(null);
  const [editedExercise, setEditedExercise] = useState<Exercise | null>(null);

  React.useEffect(() => {
    void ensureExerciseLibraryLoaded();
  }, [ensureExerciseLibraryLoaded]);

  const exercisesListQuery = usePaginatedListQuery<Exercise>({
    enabled: Boolean(loginTrainer?.id),
    queryKey: (params) =>
      queryKeys.exercises.mergedTrainerPage(loginTrainer?.id || '', {
    ...params,
    excludeDeleted: true,
  }),
    queryFn: (params) =>
      fetchMergedExercisesPageByTrainerId(loginTrainer!.id, {...params, excludeDeleted: true,}),
  });

  const filteredExercises = exercisesListQuery.items;
  const resolveMuscleIds = (exercise: Exercise): Exercise => {
    const primaryMuscleGroupIds = (exercise.primaryMuscleNames ?? [])
      .map((name) => Object.values(muscles).find((m) => m.name === name)?.id)
      .filter((id): id is string => Boolean(id));
    return { ...exercise, primaryMuscleGroupIds };
  };
  const handleOpenEditExercise = async (exercise: Exercise) => {
    const result = await fetchExerciseById(exercise.id);
    if (result.success && result.data) {
      setEditedExercise(result.data);
    } else {
      setEditedExercise(resolveMuscleIds(exercise));
    }
  };

  const handleOpenAddFromBase = async (baseExercise: Exercise) => {
    const result = await fetchExerciseById(baseExercise.id);
    const fullExercise = result.success && result.data
      ? result.data
      : resolveMuscleIds(baseExercise);
    setAddExerciseModalData({
      baseExercise: fullExercise,
      isCreatingPersonalizedExercise: true,
    });
  };

  const exercisesPagination = {
    page: exercisesListQuery.page,
    pageSize: exercisesListQuery.pageSize,
    totalElements: exercisesListQuery.meta.totalElements,
    totalPages: exercisesListQuery.meta.totalPages,
    onPageChange: exercisesListQuery.setPage,
    onPageSizeChange: exercisesListQuery.setPageSize,
    disabled: exercisesListQuery.isFetching,
  };

  const invalidateExercisesPage = () => {
    void queryClient.invalidateQueries({ queryKey: ['exercises'] });
  };

  const canDeleteExercise = useCallback(
    (exercise: Exercise) => {
      if (!loginTrainer) {
        return false;
      }
      const exerciseIsPersonalized = Boolean(exercise.isPersonalized);
      if (exerciseIsPersonalized) {
        return exercise.trainerId === loginTrainer.id;
      }
      return isMotivisionTrainer(loginTrainer);
    },
    [loginTrainer],
  );

  if (!loginTrainer) {
    return (
      <EmptyState
        icon={<AccountCircleRoundedIcon />}
        text="No Exercises For Your Role"
      />
    );
  }

  const handleAddExercise = async (
    newExercise: WithOptionalId<Exercise>,
    options: AddExerciseModalData,
  ): Promise<boolean> => {
    const success = await createExercise(newExercise, options);
    if (success) {
      setAddExerciseModalData(null);
      invalidateExercisesPage();
    }
    return success;
  };

  const handleEditExercise = async (
    oldExerciseData: Exercise,
    newExerciseData: Exercise,
  ): Promise<boolean> => {
    const success = await updateExercise(oldExerciseData, newExerciseData);
    if (!success) {
      return false;
    }
    setAddExerciseModalData(null);
    invalidateExercisesPage();
    return true;
  };

  const renderAddExerciseDialog = () => {
    if (!addExerciseModalData) return null;
    return (
      <BaseExerciseDialog
        isPersonalized={!isMotivisionTrainer(loginTrainer)}
        title="Create An Exercise"
        trainerId={loginTrainer.id}
        originalExercise={addExerciseModalData.baseExercise}
        onSubmit={({ id, ...newExercise }: WithOptionalId<Exercise>) =>
          handleAddExercise(newExercise, addExerciseModalData)
        }
        onClose={() => setAddExerciseModalData(null)}
        isOpen={Boolean(addExerciseModalData)}
      />
    );
  };

  const renderEditExerciseDialog = () => {
    if (!editedExercise?.id) return null;
    return (
      <BaseExerciseDialog
        isPersonalized={
          !isMotivisionTrainer(loginTrainer) || !!editedExercise?.isPersonalized
        }
        title="Edit Exercise"
        trainerId={editedExercise.trainerId || loginTrainer.id}
        originalExercise={editedExercise}
        onSubmit={(updatedExercise) =>
          handleEditExercise(editedExercise, {
            ...updatedExercise,
            id: editedExercise.id,
          })
        }
        onClose={() => setEditedExercise(null)}
        isOpen={!!editedExercise}
      />
    );
  };

  const deleteExercise = async (exercise: Exercise) => {
    const success = await deleteUserExercise(exercise.id);
    if (!success) {
      return;
    }

    removeItemFromPaginatedQueries<Exercise>(
      queryClient,
      ['exercises'],
      exercise.id,
    );
    invalidateExercisesPage();
  };

  const renderEmptyState = () => (
    <EmptyState
      icon={<AccountCircleRoundedIcon />}
      cta={{
        icon: <AddBoxOutlinedIcon color="primary" />,
        label: 'Add An Exercise',
        onClick: () => setAddExerciseModalData({ baseExercise: null }),
      }}
      text="No Exercises Have Been Added"
    />
  );

  if (!loginTrainer) {
    return renderEmptyState();
  }

  const renderContent = () => {
    return (
      <ListContent
        items={filteredExercises}
        isLoading={exercisesListQuery.isLoading}
        listHeaderClassName={exerciseRowStyles.listGrid}
        pagination={exercisesPagination}
        EmptyStateComponent={renderEmptyState}
        titles={exerciseListTitles}
        ItemComponent={({ data }) => (
          <ExerciseRow
            exercise={data}
            deleteExercise={deleteExercise}
            allowDelete={canDeleteExercise(data)}
            openEditExerciseModal={() => handleOpenEditExercise(data)}
            selected={selectedExercise === data.id}
            onSelect={() => setSelectedExercise(data.id)}
            key={data.id}
            exerciseTrainer={Object.values(trainers)
              .map(({ trainer }) => trainer)
              .find(({ id }) => data.trainerId === id)}
            addExerciseFromBaseExercise={(baseExercise) => handleOpenAddFromBase(baseExercise)}
          />
        )}
      />
    );
  };

  const getSearchPlaceholder = () => {
    if (!filteredExercises.length) return '';
    return 'Search for Exercises';
  };

  return (
    <ContentDrill
      title="Exercise List"
      isParent
      actions={[
        {
          icon: <AddBoxOutlinedIcon />,
          label: 'Add An Exercise',
          onClick: () => setAddExerciseModalData({ baseExercise: null }),
        },
      ]}
      searchPlaceholder={getSearchPlaceholder()}
      searchQuery={exercisesListQuery.searchQuery}
      setSearchQuery={exercisesListQuery.setSearchQuery}
      disableSearch={false}
    >
      {renderContent()}
      {renderAddExerciseDialog()}
      {renderEditExerciseDialog()}
    </ContentDrill>
  );
};

export default TrainerExercisesContent;
